/**
 * 파일명 : usr0301m.tsx
 * 설명   : 경력현황
 * 수정내역
 ************************************************
 * Version    수정일자      수정자    수정내용      
 ************************************************
 * 1.0       2026-03-24    김시현    최초작성
 *     
 */
/************************************************
 * Import 영역  
 ************************************************/
/*  React  */
import React from 'react';
import locale from 'antd/lib/locale/ko_KR';

/* Reducer */
import { useAppSelector, useAppDispatch } from '../../../redux/hooks';
import { Usr0301mAction } from '../../../redux/slice/usr/usr0301m_slice';
import { IUserItemState, IUserExpreState, ITecnlLvlState, ICertItemState, IDeptCdState, IExTecnlLvlState } from '../../../redux/models/usr/usr0301m_model';

/*  AntD  */
import { Button, Space, Row, Col, Layout, Input, Typography, Collapse, ConfigProvider, Select, Table } from 'antd';
import { SearchOutlined, CaretRightOutlined } from '@ant-design/icons';

/*  업무  */
import * as commonProxy from 'libs/CommonProxy';
import * as common from 'libs/common';
import * as commonDate from 'libs/commonDate';
import ExportExcelFile from 'components/exportExcelFile';

/************************************************
 * 전역변수 영역
 ************************************************/
const Usr0301m = () => {
    /************************************************
     * 화면 초기화 영역  
     ************************************************/
    /**
     * 기능 : State / Reducer 선언
     * param : 
     */
    const dispatch       = useAppDispatch();
    const inquiryCnds    = useAppSelector((state) => state.Usr0301m.inquiryCnds);    // 조회조건
    const userList       = useAppSelector((state) => state.Usr0301m.userList);       // 직원명
    const deptCdList     = useAppSelector((state) => state.Usr0301m.deptCdList);     // 부서명
    const certList       = useAppSelector((state) => state.Usr0301m.certList);       // 자격증
    const tecnlLvlList   = useAppSelector((state) => state.Usr0301m.tecnlLvlList);   // 기술등급
    const ExTecnlLvlList = useAppSelector((state) => state.Usr0301m.exTecnlLvlList); // 예상기술등급
    const userExpre      = useAppSelector((state) => state.Usr0301m.userExpre);      // 경력현황

    let tmp = 0;
    const [tableKey, setTableKey] = React.useState<number>(0);
    const [contentHeight, setContentHeight] = React.useState<number>(window.innerHeight);

    /* Render 변수 선언영역 */
    /* AntD */
    const { Content } = Layout;
    const { Panel } = Collapse;
    const { Text } = Typography;

    /**
     * 기능 : componentDidMount
     * 설명 : 화면 최초 로드시에 필요한 기능을 구현한다.
     * param : 
     * return : None
     */
    React.useEffect(() => {
        const headerHeight = 250;  // Header 높이
        setContentHeight(window.innerHeight - headerHeight);

        const handleResize = () => {
            let tmpKey = ++tmp;
            tmp = tmpKey;
            setTableKey(tmpKey);
        };

        fn_ResetResult();    // 초기화
        fn_User();           // 직원명
        setTecnlLvlList();   // 프로젝트등급코드(공통코드) set
        setExTecnlLvlList(); // 예상프로젝트등급코드(공통코드) set
        setCertList();       // 자격증구분코드(공통코드) set
        setDeptCdsList();    // 부서
        fn_Search();

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize); // cleanup
        };
    }, []);

    /* Table Columns */
    const columns = React.useCallback(() => {
        const arr:any = [
            {
                title: () => <div style={{ textAlign: 'center' }}>직원명</div>,
                dataIndex: 'userNm',
                key: 'userNm',
                align: 'left',
                width: '8%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>생년월일</div>,
                dataIndex: 'birthDt',
                key: 'birthDt',
                align: 'center',
                render: (text:string) => commonDate.maskDate(text, 'YYYY-MM-DD'),
                width: '6%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>기술등급</div>,
                dataIndex: 'tecnlLvlCd',
                key: 'tecnlLvlCd',
                align: 'center',
                width: '6%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>예상기술등급</div>,
                dataIndex: 'exTecnlLvlCd',
                key: 'exTecnlLvlCd',
                align: 'center',
                width: '6%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>자격증</div>,
                dataIndex: 'certTpCd',
                key: 'certTpCd',
                align: 'left',
                width: '12%',
            },
            //{
            //    title: () => <div style={{ textAlign: 'center' }}>프로젝트경력</div>,
            //    dataIndex: 'prjExpreMm',
            //    key: 'prjExpreMm',
            //    align: 'right',
            //    width: '7%',
            //},
            {
                title: () => <div style={{ textAlign: 'center' }}>근무경력</div>,
                dataIndex: 'workExpreMm',
                key: 'workExpreMm',
                align: 'center',
                width: '6%',
                render: (_text:number) => `${Math.floor(_text/12)}년 ${_text%12}개월`

            },
            // {
            //     title: () => <div style={{ textAlign: 'center' }}>전문기술</div>,
            //     dataIndex: 'exprisCntnt',
            //     key: 'exprisCntnt',
            //     align: 'left',
            //     width: '0%',
            // },
            {
                title: () => <div style={{ textAlign: 'center' }}>부서</div>,
                dataIndex: 'deptNm',
                key: 'deptNm',
                align: 'left',
                width: '10%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>직위</div>,
                dataIndex: 'postNm',
                key: 'postNm',
                align: 'center',
                width: '6%',
            },
            // {
            //     title: () => <div style={{ textAlign: 'center' }}>입사일자</div>,
            //     dataIndex: 'realEntDt',
            //     key: 'realEntDt',
            //     align: 'center',
            //     render: (text:string) => commonDate.maskDate(text, 'YYYY-MM-DD'),
            //     width: '0%',
            // },
            {
                title: () => <div style={{ textAlign: 'center' }}>최종학력</div>,
                dataIndex: 'eduTpCd',
                key: 'eduTpCd',
                align: 'left',
                width: '6%',
            },
            // {
            //     title: () => <div style={{ textAlign: 'center' }}>졸업년월</div>,
            //     dataIndex: 'gradYm',
            //     key: 'gradYm',
            //     align: 'center',
            //     render: (text:string) => commonDate.maskDate(text, 'YYYY-MM-DD'),
            //     width: '0%',
            // },
            {
                title: () => <div style={{ textAlign: 'center' }}>상세보기</div>,
                key: 'action',
                align: 'center',
                width: '6%',
                render: (_text:string, record:IUserExpreState) => (
                    common.isAdmin() || common.getEmpNo() === record.empNo ?
                    <Button
                        id='btn_showDetail'
                        name='btn_showDetail'
                        className='background-color-default-button'
                        size='small'
                        onClick={(e) => onClick(e, record)}
                    >
                        상세보기
                    </Button> : null
                    ),
                }];
            return arr;
    }, []);
    

    /* 엑셀용 데이터 가공 */
    const excelDatas = userExpre.map(item => ({
        ...item,
        workExpreMm: Math.floor(Number(item.workExpreMm)/12) + '년 ' + Number(item.workExpreMm)%12 + '개월'
    }));

    /**
     * 기능 : fn_User
     * 설명 : 직원명 콤보박스 데이터 조회
     * param : 
     * return : None
     **/
    const fn_User = () => {
        /* 변수선언 및 셋팅 영역 */
        // 입력파라미터
        const requestData = {
            userStCd: 'A' // 활동상태
        };

        /* 트렌젝션 호출 및 결과 셋팅 */
        commonProxy.callAsyncSvc(requestData, {
            txCd: 'TRUSRR0003', // 사용자 조회
            success: (responseData:any) => {
                // 사용자목록 이름순으로 정렬
                responseData.userList.sort((a:IUserItemState, b:IUserItemState) => (a.userNm > b.userNm) ? 1 : -1)
                dispatch(Usr0301mAction.setUserList(responseData.userList));
            },
        });
    };
    
    /**
     * 기능 : userOptions
     * 설명 : 직원명 set
     * param : 
     * return : None
     **/
    const userOptions = React.useCallback(() => {
        const arrUserOptions = [{label: '전체', value: ''}];

        userList.forEach((item:IUserItemState) => {
            arrUserOptions.push({
                label: item.userNm,
                value: item.empNo
            });
        });

        return arrUserOptions;
    }, [userList]);

    
    /**
     * 기능 : setTecnlLvlList
     * 설명 : 프로젝트등급코드(공통코드) set
     * param : 
     * return : None
     **/
    const setTecnlLvlList = () => {
        const option = {
            useYn: 'Y',
            callback: (datas:ITecnlLvlState[]) => {
                let arrOptions:ITecnlLvlState[] = [];

                arrOptions.push({label: '전체', value: ''});

                datas.forEach((item:ITecnlLvlState) => {
                    arrOptions.push({
                        label: item.cdNm,
                        value: item.cd
                    });
                });
                dispatch(Usr0301mAction.setTecnlLvlList(arrOptions));
            }
        };
        common.getComCodeList('025', option);
    }

    /**
     * 기능 : setExTecnlLvlList
     * 설명 : 예상프로젝트등급코드(공통코드) set
     * param : 
     * return : None
     **/
    const setExTecnlLvlList = () => {
        const option = {
            useYn: 'Y',
            callback: (datas:IExTecnlLvlState[]) => {
                let arrOptions:IExTecnlLvlState[] = [];

                arrOptions.push({label: '전체', value: ''});

                datas.forEach((item:IExTecnlLvlState) => {
                    arrOptions.push({
                        label: item.cdNm,
                        value: item.cd
                    });
                });
                dispatch(Usr0301mAction.setExTecnlLvlList(arrOptions));
            }
        };
        common.getComCodeList('025', option);
    }

    /**
     * 기능 : setCertList
     * 설명 : 자격증구분코드(공통코드) set
     * param : 
     * return : None
     **/
    const setCertList = () => {
        const option = {
            useYn: 'Y',
            callback: (datas:ICertItemState[]) => {
                let arrOptions:ICertItemState[] = [];

                arrOptions.push({label: '전체', value: ''});

                datas.forEach((item:ICertItemState) => {
                    arrOptions.push({
                        label: item.cdNm,
                        value: item.cd
                    });
                });
                dispatch(Usr0301mAction.setCertList(arrOptions));
            }
        };
        common.getComCodeList('023', option);
    }

    /**
     * 기능 : setDeptCdsList
     * 설명 : 부서코드
     * param : 
     * return : None
     **/
    const setDeptCdsList = () => {

        const requestData = {
            deptStCd: 'A'
        };

        /* 트렌젝션 호출 및 결과 셋팅 */
        commonProxy.callAsyncSvc(requestData, {
            txCd: 'TRCOMR0005',
            success: (responseData:any) => {
                // 사용자목록 이름순으로 정렬
                responseData.deptItems.sort((a:IDeptCdState, b:IDeptCdState) => (a.deptNm > b.deptNm) ? 1 : -1)
                dispatch(Usr0301mAction.setDeptCdList(responseData.deptItems));
            },
        });

    };

    /* 부서 콤보박스 */
    const deptOptions = React.useCallback(() => {
        let arrOptions: IDeptCdState[] = [];

        arrOptions.push({label: '전체', value: ''});

        deptCdList.forEach((item:IDeptCdState) => {
            arrOptions.push({
                label: item.deptNm,
                value: item.deptCd
            });
        });

        return arrOptions;
    }, [deptCdList]);

    /* 엑셀다운로드 */
    const excelParams = {
        datas: excelDatas,
        btnName: '엑셀파일 내려받기',
        fileName: '경력현황_' + commonDate.getTodayFormat('YYYYMMDD'),
        sheetName:'경력현황_' + commonDate.getTodayFormat('YYYYMMDD'),
        columns: columns(),
    }

        /**
         * 기능 : 초기화 이벤트 선언
         * param : 
         */
        const fn_Reset = () => {
            dispatch(Usr0301mAction.setCurrent(1));
            dispatch(Usr0301mAction.initState());
        };
    
        /**
         * 기능 : 결과초기화 이벤트 선언
         * param : 
         */
        const fn_ResetResult = () => {
            dispatch(Usr0301mAction.setCurrent(1));
            dispatch(Usr0301mAction.setUserExpre([]));
        };

        /**
         * 기능 : 검색조회
         * param : 
         */
        const fn_Search  = () => {
            /* 변수선언 및 셋팅 영역 */
            /* 입력파라미터 */
            const requestData = {
                ...inquiryCnds
            };

            /* 트렌젝션 호출 및 결과 셋팅 */
            commonProxy.callAsyncSvc(requestData, {
                txCd: 'TRUSRR0011', /* 경력현황(조회) */
                success: (responseData:any) => {
                    dispatch(Usr0301mAction.setUserExpre(responseData.userExpre));
                },
            });
        };
    
        /**
         * 기능 : onClick 이벤트 선언
         * param : 
         */
        const onClick = async (e:any, record:IUserExpreState) => {
            const buttonName = e.currentTarget.name;
            switch (buttonName) {
                /* 조회 버튼 */
                case 'btn_Search': {
                fn_ResetResult();
                fn_Search();
                break;
                }
                /* 초기화 버튼 */
                case 'btn_Reset': {
                fn_Reset();
                break;
                }
                /* 상세보기 버튼 */
                case 'btn_showDetail': {
                    const param = {
                        menuId : 'MUSR2023001', // MUSR2023001(경력관리)
                        props : {
                            instCd: '002',
                            empNo : record.empNo,
                            birdDt : record.birthDt,
                            /*exTecnlLvlCd : record.exTecnlLvlCd*/
                        }
                    };
                    common.openPage(param);
                    break;
                }
            }
        };
    
    /* 화면 디자인 영역 */
    return (
        <Layout>
            <Content>
                    <Collapse
                        bordered={false}
                        defaultActiveKey={['1']}
                        expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
                        className='site-collapse-custom-collapse'
                    >
                        <Panel header='검색조건' key='1' className='site-collapse-custom-panel'>
                            <Row
                                align='middle'
                                justify='space-between'
                                style={{ marginTop: 10 }}
                            >
                                <Space>
                                    <Col>
                                        <Space>
                                            <label>직원명</label>
                                            <Select
                                                style={{
                                                    width: 210,
                                                }}
                                                showSearch
                                                optionFilterProp='label'
                                                defaultValue=''
                                                filterSort={(optionA: any, optionB: any) => {
                                                    const labelA = (optionA?.label ?? '').toLowerCase();
                                                    const labelB = (optionB?.label ?? '').toLowerCase();
                                                    
                                                    if (labelA === '전체') return -1;
                                                    if (labelB === '전체') return 1;
                                                    
                                                    return labelA.localeCompare(labelB);
                                                }}
                                                options={userOptions()}
                                                value={inquiryCnds.empNo}
                                                onChange={val => dispatch(Usr0301mAction.setInquiryCnds({...inquiryCnds, empNo: val}))}
                                            ></Select>
                                        </Space>
                                    </Col>
                                    <Col>
                                        <Space>
                                            <label style={{ marginRight: 13 }}>부서명</label>
                                            <Select
                                                style={{
                                                    width: 210,
                                                }}
                                                showSearch
                                                optionFilterProp='label'
                                                options={deptOptions()}
                                                value={inquiryCnds.deptCd}
                                                onChange={val => {
                                                    dispatch(Usr0301mAction.setInquiryCnds({...inquiryCnds, deptCd: val}))
                                                }}
                                            />
                                        </Space>
                                    </Col>
                                    <Col>
                                        <Space>
                                            <label style={{ marginRight: 26 }}>근무경력</label>
                                            <ConfigProvider locale={locale}>
                                                <Input
                                                placeholder="개월(시작)"
                                                style={{ width: 99 }}
                                                value={inquiryCnds.prjExpreStMm}
                                                onChange={(e) => dispatch(Usr0301mAction.setInquiryCnds({
                                                    ...inquiryCnds,
                                                    prjExpreStMm: e.target.value
                                                }))}
                                                type="number"
                                                />
                                                <label>~</label>
                                                <Input
                                                placeholder="개월(종료)"
                                                style={{ width: 99 }}
                                                value={inquiryCnds.prjExpreEndMm}
                                                onChange={(e) => dispatch(Usr0301mAction.setInquiryCnds({
                                                    ...inquiryCnds,
                                                    prjExpreEndMm: e.target.value
                                                }))}
                                                type="number"
                                                />
                                            </ConfigProvider>
                                        </Space>
                                    </Col>
                                </Space>
                                
                            </Row>
                            <Row
                                align='middle'
                                justify='space-between'
                                style={{ marginTop: 10 }}
                            >
                                <Space>
                                    <Col>
                                        <Space>
                                            <label>자격증</label>
                                            <Select
                                                style={{
                                                    width: 210,
                                                }}
                                                optionFilterProp='label'
                                                options={certList}
                                                onChange={val => {
                                                    dispatch(Usr0301mAction.setInquiryCnds({...inquiryCnds, certTpCd: val}))
                                                }}
                                                value={inquiryCnds.certTpCd}
                                            />
                                        </Space>
                                    </Col>
                                    <Col>
                                        <Space>
                                            <label>기술등급</label>
                                            <Select
                                                style={{
                                                    width: 210,
                                                }}
                                                optionFilterProp='label'
                                                options={tecnlLvlList}
                                                onChange={val => {
                                                    dispatch(Usr0301mAction.setInquiryCnds({...inquiryCnds, tecnlLvlCd: val}))
                                                }}
                                                value={inquiryCnds.tecnlLvlCd}
                                            />
                                        </Space>
                                    </Col>
                                    <Col>
                                        <Space>
                                            <label>예상기술등급</label>
                                            <Select
                                                style={{
                                                    width: 209,
                                                }}
                                                optionFilterProp='label'
                                                options={ExTecnlLvlList}
                                                onChange={val => {
                                                    dispatch(Usr0301mAction.setInquiryCnds({...inquiryCnds, exTecnlLvlCd: val}))
                                                }}
                                                value={inquiryCnds.exTecnlLvlCd}
                                            />
                                        </Space>
                                    </Col>
                                </Space>
                                <Col>
                                    <Space>
                                        <Button
                                            name='btn_Reset'
                                            className='background-color-default-button'
                                            onClick={(e:any) => onClick(e, null)}
                                        >
                                            초기화
                                        </Button>
                                        <Button
                                            name='btn_Search'
                                            className='background-color-primary-button'
                                            icon={<SearchOutlined />}
                                            onClick={(e:any) => onClick(e, null)}
                                        >
                                            조회
                                    </Button>
                                    </Space>
                                </Col>
                            </Row>
                        </Panel>
                    </Collapse>
                    <Row align='middle' style={{ height: 50 }}>
                        <Col flex='9.5'>
                            <Space>
                                <img className='title-icon' alt=''/>
                                <Text strong>경력현황</Text>
                            </Space>
                        </Col>
                        <Col flex='0.5' >
                            <ExportExcelFile excelParams={excelParams}/>
                        </Col>
                    </Row>
                    <Row>
                        <Table
                            dataSource={userExpre}
                            key='empNo'
                            columns={columns()}
                            size='small'
                            style={{width:'100%'}}
                            scroll={{y:550}}
                        />
                    </Row>
            </Content>
        </Layout>
    );
};

export default Usr0301m;

